/* Sidebar et MobileSheet — navigation latérale de l'application Factumis.

   Sidebar    : panneau gauche desktop, collapsable (classe CSS `.collapsed` sur
                `.app`, les labels navitem se cachent via CSS). Contient le logo,
                les items de navigation (NavItems) et le bloc utilisateur (UserBlock).
   MobileSheet: drawer plein-écran pour mobile, contrôlé par les props open/onClose.
                Réutilise NavItems et UserBlock pour rester DRY.
   NavItems   : rendu des groupes NAV ; surligne l'item actif via activeNavId().
   UserBlock  : affiche initiales + nom + org, bouton de déconnexion. */
import { Fragment, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Icon } from '../lib/icons';
import { authClient, useSession } from '../lib/auth-client';
import { NAV, activeNavId, useGo } from './nav';

function initialsFrom(name?: string | null, email?: string | null): string {
  const n = (name ?? '').trim();
  if (n) {
    const parts = n.split(/\s+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((p) => p[0]);
    return letters.join('').toUpperCase();
  }
  const e = (email ?? '').trim();
  return e ? e[0].toUpperCase() : '?';
}

export function NavItems({ onNavigate }: { onNavigate?: () => void }) {
  const go = useGo();
  const { pathname } = useLocation();
  const active = activeNavId(pathname);
  return (
    <>
      {NAV.map((g, gi) => (
        <Fragment key={gi}>
          <div className="side__grp">{g.grp}</div>
          {g.items.map((it) => (
            <button
              key={it.id}
              className={'navitem' + (active === it.id ? ' active' : '')}
              onClick={() => {
                go(it.id);
                onNavigate?.();
              }}
              title={it.label}
            >
              <Icon name={it.icon} size={19} />
              <span className="navitem__label">{it.label}</span>
              {it.badge ? <span className="badge-n">{it.badge}</span> : null}
            </button>
          ))}
        </Fragment>
      ))}
    </>
  );
}

function UserBlock() {
  const navigate = useNavigate();
  const session = useSession();
  const activeOrg = authClient.useActiveOrganization();

  const [signingOut, setSigningOut] = useState(false);

  const user = session.data?.user;
  const name = user?.name?.trim() || user?.email || 'Mon compte';
  const orgName = activeOrg.data?.name || '';

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      await authClient.signOut();
    } finally {
      navigate('/connexion');
    }
  };

  return (
    <div className="side__user">
      <span className="avatar">{initialsFrom(user?.name, user?.email)}</span>
      <span className="who">
        <b>{name}</b>
        <span>{orgName}</span>
      </span>
      <button
        className="side__signout"
        aria-label="Se déconnecter"
        title="Se déconnecter"
        onClick={handleSignOut}
        disabled={signingOut}
      >
        <Icon name="logout" size={17} />
      </button>
    </div>
  );
}

export function Sidebar({ collapsed, toggleCollapse }: { collapsed: boolean; toggleCollapse: () => void }) {
  const label = collapsed ? 'Déployer le menu' : 'Replier le menu';
  return (
    <aside className="side">
      <div className="side__top">
        <span className="side__logo">Factumis</span>
        <button className="side__collapse" onClick={toggleCollapse} aria-label={label} title={label}>
          <Icon name="sidebar" size={17} />
        </button>
      </div>
      <nav className="side__nav">
        <NavItems />
      </nav>
      <UserBlock />
    </aside>
  );
}

export function MobileSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <>
      <div className={'scrim' + (open ? ' on' : '')} onClick={onClose} />
      <aside className={'msheet' + (open ? ' on' : '')}>
        <div className="side__top">
          <span className="side__logo">Factumis</span>
          <button className="side__collapse" style={{ display: 'inline-flex' }} onClick={onClose} aria-label="Fermer">
            <Icon name="x" size={18} />
          </button>
        </div>
        <nav className="side__nav">
          <NavItems onNavigate={onClose} />
        </nav>
        <UserBlock />
      </aside>
    </>
  );
}
